// src/constants/scoringConstants.js

/**
 * Constantes para los selects del módulo de Scoring
 */

export const TIPO_PERSONA = [
  { id: 1, descripcion: 'Persona Física' },
  { id: 2, descripcion: 'Persona Jurídica' },
];

export const GENERO = [
  { id: 1, descripcion: 'Masculino' },
  { id: 2, descripcion: 'Femenino' },
];

export const ESTADO_CIVIL = [
  { id: 1, descripcion: 'Soltero' },
  { id: 2, descripcion: 'Casado' },
];

export const TIPO_INGRESO = [
  { id: 1, descripcion: 'Empleado' },
  { id: 2, descripcion: 'Monotributo' },
  { id: 3, descripcion: 'Responsable Inscripto' },
];

export const CATEGORIA_FISCAL = [
  { id: 1, descripcion: 'Consumidor Final' },
  { id: 2, descripcion: 'Responsable Monotributo' },
  { id: 3, descripcion: 'IVA Responsable Inscripto' },
  { id: 4, descripcion: 'IVA Sujeto Exento' },
];

/**
 * Tipos de documentos estandarizados
 * Nombres estándar para los documentos del sistema de scoring
 */
export const DOCUMENT_TYPES = {
  DNI: 'DNI',
  INGRESOS: 'INGRESOS',
  FORMULARIO_711: 'FORMULARIO_711',
  INSCRIPCION_MONOTRIBUTO: 'INSCRIPCION_MONOTRIBUTO',
  PAGO_MONOTRIBUTO: 'PAGO_MONOTRIBUTO',
  PAGO_GANANCIAS: 'PAGO_GANANCIAS',
  // Otros documentos adicionales
  CUIT: 'CUIT',
  FORMULARIO_713: 'FORMULARIO_713',
  PAGO_IVA: 'PAGO_IVA'
};

/**
 * Sufijos para identificar el tipo de persona en los documentos
 */
export const PERSON_SUFFIXES = {
  TITULAR: '_TITULAR',
  GARANTE_1: '_GARANTE_1',
  GARANTE_2: '_GARANTE_2'
};

/**
 * Función para generar el nombre estandarizado del documento
 * @param {string} documentType - Tipo de documento (usar constantes de DOCUMENT_TYPES)
 * @param {string} personSuffix - Sufijo de persona (usar constantes de PERSON_SUFFIXES)
 * @param {string} fileExtension - Extensión del archivo (opcional, ej: 'pdf', 'jpg')
 * @returns {string} Nombre estandarizado del documento
 * 
 * Ejemplo: getStandardDocumentName(DOCUMENT_TYPES.DNI, PERSON_SUFFIXES.TITULAR, 'pdf')
 * Retorna: "DNI_TITULAR.pdf"
 */
export const getStandardDocumentName = (documentType, personSuffix, fileExtension = '') => {
  if (fileExtension) {
    // Remover el punto si viene incluido
    const ext = fileExtension.startsWith('.') ? fileExtension : `.${fileExtension}`;
    return `${documentType}${personSuffix}${ext}`;
  }
  return `${documentType}${personSuffix}`;
};

/**
 * Mapeo de tipos de documento internos a nombres estándar
 * Permite migrar gradualmente el código existente
 */
export const DOCUMENT_TYPE_MAPPING = {
  'dni': DOCUMENT_TYPES.DNI,
  'recibos': DOCUMENT_TYPES.INGRESOS,
  'inscripcionMonotributo': DOCUMENT_TYPES.INSCRIPCION_MONOTRIBUTO,
  'pagosMonotributo': DOCUMENT_TYPES.PAGO_MONOTRIBUTO,
  'pagosGanancias': DOCUMENT_TYPES.PAGO_GANANCIAS,
  'formulario711': DOCUMENT_TYPES.FORMULARIO_711,
  'cuit': DOCUMENT_TYPES.CUIT,
  'formulario713': DOCUMENT_TYPES.FORMULARIO_713,
  'pagosIVA': DOCUMENT_TYPES.PAGO_IVA
};

/**
 * Mapeo de tipos de persona a sufijos estándar
 */
export const PERSON_TYPE_MAPPING = {
  'titular': PERSON_SUFFIXES.TITULAR,
  'garante1': PERSON_SUFFIXES.GARANTE_1,
  'garante2': PERSON_SUFFIXES.GARANTE_2,
  'garante_1': PERSON_SUFFIXES.GARANTE_1,
  'garante_2': PERSON_SUFFIXES.GARANTE_2
};

/**
 * Función helper para obtener el valor del select a partir del texto completo
 * Ejemplo: "1 - Persona Física" => 1
 */
export const extractIdFromText = (text) => {
  if (!text) return '';
  const match = text.match(/^(\d+)\s*-/);
  return match ? match[1] : text;
};

/**
 * Función helper para obtener la descripción del select a partir del texto completo
 * Ejemplo: "1 - Persona Física" => "Persona Física"
 */
export const extractDescriptionFromText = (text) => {
  if (!text) return '';
  const match = text.match(/^\d+\s*-\s*(.+)$/);
  return match ? match[1] : text;
};

/**
 * Función helper para formatear el valor del select
 * Ejemplo: (1, "Persona Física") => "1 - Persona Física"
 */
export const formatSelectValue = (id, descripcion) => {
  return `${id} - ${descripcion}`;
};

/**
 * Funciones para obtener el texto completo a partir del ID
 */
export const getEstadoCivilTexto = (id) => {
  const item = ESTADO_CIVIL.find(e => e.id === parseInt(id));
  return item ? `${item.id} - ${item.descripcion}` : id;
};

export const getGeneroTexto = (id) => {
  const item = GENERO.find(e => e.id === parseInt(id));
  return item ? `${item.id} - ${item.descripcion}` : id;
};

export const getTipoIngresoTexto = (id) => {
  const item = TIPO_INGRESO.find(e => e.id === parseInt(id));
  return item ? `${item.id} - ${item.descripcion}` : id;
};

export const getCategoriaFiscalTexto = (id) => {
  const item = CATEGORIA_FISCAL.find(e => e.id === parseInt(id));
  return item ? `${item.id} - ${item.descripcion}` : id;
};

export const getTipoPersonaTexto = (id) => {
  const item = TIPO_PERSONA.find(e => e.id === parseInt(id));
  return item ? `${item.id} - ${item.descripcion}` : id;
};
